import { getApiError } from '../../utils/errorHelper';
import React, { useState, useEffect, useCallback } from 'react';
import { FormInput, Plus, Pencil, Trash2, Save, X, Loader2, CheckCircle, AlertCircle, Type, Hash, Calendar, List, CheckSquare, ToggleLeft } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { informationFieldsApi } from '../../services/api';

const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: Type, description: 'Single line text input' },
  { value: 'textarea', label: 'Text Area', icon: Type, description: 'Multi-line text input' },
  { value: 'number', label: 'Number', icon: Hash, description: 'Numeric input' },
  { value: 'currency', label: 'Currency', icon: Hash, description: 'Currency amount' },
  { value: 'date', label: 'Date', icon: Calendar, description: 'Date picker' },
  { value: 'dropdown', label: 'Dropdown', icon: List, description: 'Select from options' },
  { value: 'checkbox', label: 'Checkbox', icon: CheckSquare, description: 'Multiple selections' },
  { value: 'yes_no', label: 'Yes/No', icon: ToggleLeft, description: 'Boolean toggle' },
  { value: 'email', label: 'Email', icon: Type, description: 'Email address' },
  { value: 'phone', label: 'Phone', icon: Type, description: 'Phone number' },
];

const emptyForm = {
  name: '',
  label: '',
  description: '',
  field_type: 'text',
  options: [],
  placeholder: '',
  is_required_default: true
};

// Separate InformationFieldForm component to prevent re-render issues
const InformationFieldForm = ({ initialData, onSave, onCancel, isNew, saving }) => {
  const [form, setForm] = useState(initialData || emptyForm);
  const [optionsInput, setOptionsInput] = useState(
    (initialData?.options || []).join(', ')
  );

  const handleFieldChange = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleOptionsChange = useCallback((value) => {
    setOptionsInput(value);
    const options = value.split(',').map(o => o.trim()).filter(o => o.length > 0);
    setForm(prev => ({ ...prev, options }));
  }, []);

  const handleSubmit = () => {
    onSave(form);
  };

  const showOptions = form.field_type === 'dropdown' || form.field_type === 'checkbox';

  return (
    <div className="bg-slate-900/80 rounded-lg p-4 border border-purple-500/30 space-y-4">
      <div className="flex items-center gap-2">
        <FormInput className="w-4 h-4 text-purple-400" />
        <span className="text-white font-medium">{isNew ? 'Add Information Field' : 'Edit Information Field'}</span>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-slate-400 block mb-1">Internal Name *</label>
          <input 
            type="text"
            placeholder="e.g., company_pan, director_din"
            value={form.name}
            onChange={(e) => handleFieldChange('name', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
            className="w-full h-10 rounded-md bg-slate-800 border border-slate-700 text-white text-sm px-3 font-mono focus:outline-none focus:border-purple-500"
          />
        </div>
        <div>
          <label className="text-xs text-slate-400 block mb-1">Display Label *</label>
          <input 
            type="text"
            placeholder="e.g., Company PAN Number"
            value={form.label}
            onChange={(e) => handleFieldChange('label', e.target.value)}
            className="w-full h-10 rounded-md bg-slate-800 border border-slate-700 text-white text-sm px-3 focus:outline-none focus:border-purple-500"
          />
        </div>
      </div>

      <div>
        <label className="text-xs text-slate-400 block mb-1">Description</label>
        <input 
          type="text"
          placeholder="Help text for users"
          value={form.description}
          onChange={(e) => handleFieldChange('description', e.target.value)}
          className="w-full h-10 rounded-md bg-slate-800 border border-slate-700 text-white text-sm px-3 focus:outline-none focus:border-purple-500"
        />
      </div>

      <div>
        <label className="text-xs text-slate-400 block mb-2">Field Type</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
          {FIELD_TYPES.map(type => {
            const Icon = type.icon;
            return (
              <button
                key={type.value}
                type="button"
                onClick={() => handleFieldChange('field_type', type.value)}
                className={`p-2 rounded-lg border text-left transition-all ${
                  form.field_type === type.value
                    ? 'bg-purple-600/20 border-purple-500 text-white'
                    : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:border-slate-600'
                }`}
              >
                <Icon className="w-4 h-4 mb-1" />
                <p className="text-xs font-medium">{type.label}</p>
              </button>
            );
          })}
        </div>
      </div>

      {showOptions && (
        <div>
          <label className="text-xs text-slate-400 block mb-1">Options (comma separated)</label>
          <input 
            type="text"
            placeholder="Option 1, Option 2, Option 3"
            value={optionsInput}
            onChange={(e) => handleOptionsChange(e.target.value)}
            className="w-full h-10 rounded-md bg-slate-800 border border-slate-700 text-white text-sm px-3 focus:outline-none focus:border-purple-500"
          />
          {form.options.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {form.options.map((opt, idx) => (
                <span key={idx} className="text-xs bg-purple-600/30 text-purple-300 px-2 py-0.5 rounded">
                  {opt}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-slate-400 block mb-1">Placeholder</label>
          <input 
            type="text"
            placeholder="e.g., Enter your PAN number"
            value={form.placeholder}
            onChange={(e) => handleFieldChange('placeholder', e.target.value)}
            className="w-full h-10 rounded-md bg-slate-800 border border-slate-700 text-white text-sm px-3 focus:outline-none focus:border-purple-500"
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_required_default}
              onChange={(e) => handleFieldChange('is_required_default', e.target.checked)}
              className="rounded bg-slate-700 border-slate-600"
            />
            <span className="text-sm text-slate-300">Required by default</span>
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={onCancel} className="border-slate-600 text-slate-300">
          <X className="w-4 h-4 mr-1" /> Cancel
        </Button>
        <Button size="sm" onClick={handleSubmit} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          {isNew ? 'Create' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

// Main InformationFieldsTab component
const InformationFieldsTab = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadFields();
  }, []);

  const loadFields = async () => {
    setLoading(true);
    try {
      const data = await informationFieldsApi.getAllAdmin();
      setFields(data || []);
    } catch (error) {
      toast.error('Failed to load information fields');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingId(null);
    setEditingField(null);
    setShowForm(true);
  };

  const handleEdit = (field) => {
    setEditingId(field.id);
    setEditingField({
      name: field.name || '',
      label: field.label || '',
      description: field.description || '',
      field_type: field.field_type || 'text',
      options: field.options || [],
      placeholder: field.placeholder || '',
      is_required_default: field.is_required_default !== false
    });
    setShowForm(true);
  };

  const handleSave = async (formData) => {
    if (!formData.name?.trim() || !formData.label?.trim()) {
      toast.error('Name and Label are required');
      return;
    }
    
    if ((formData.field_type === 'dropdown' || formData.field_type === 'checkbox') && formData.options.length === 0) {
      toast.error('Dropdown and Checkbox fields require options');
      return;
    }
    
    setSaving(true);
    try {
      if (editingId) {
        await informationFieldsApi.update(editingId, formData);
        toast.success('Information field updated');
      } else {
        await informationFieldsApi.create(formData);
        toast.success('Information field created');
      }
      setShowForm(false);
      setEditingId(null);
      setEditingField(null);
      loadFields();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to save information field'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setEditingField(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this information field?')) return;
    
    try {
      await informationFieldsApi.delete(id);
      toast.success('Information field deleted');
      loadFields();
    } catch (error) {
      toast.error('Failed to delete information field');
    }
  };

  const getFieldTypeConfig = (type) => FIELD_TYPES.find(t => t.value === type) || FIELD_TYPES[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Information Fields</h2>
          <p className="text-slate-400 text-sm mt-1">Manage information fields that can be required for products</p>
        </div>
        {!showForm && (
          <Button onClick={handleCreateNew} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="w-4 h-4 mr-2" /> Add Information Field
          </Button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <InformationFieldForm
          initialData={editingField}
          onSave={handleSave}
          onCancel={handleCancel}
          isNew={!editingId}
          saving={saving}
        />
      )}

      {/* Fields List */}
      {!showForm && (
        loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
          </div>
        ) : fields.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {fields.map(field => (
              <div key={field.id} className={`bg-slate-800/50 border rounded-lg p-4 ${
                field.is_active ? 'border-slate-700/50' : 'border-red-500/30 opacity-60'
              }`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {React.createElement(getFieldTypeConfig(field.field_type).icon, {
                        className: "w-4 h-4 text-purple-400"
                      })}
                      <h3 className="text-white font-medium">{field.label}</h3>
                      {!field.is_active && (
                        <span className="text-xs bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded">Inactive</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 font-mono mb-2">{field.name}</p>
                    {field.description && (
                      <p className="text-slate-400 text-sm mb-2">{field.description}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="text-xs bg-purple-600/30 text-purple-300 px-1.5 py-0.5 rounded">
                        {getFieldTypeConfig(field.field_type).label}
                      </span>
                      {field.options && field.options.length > 0 && (
                        <span className="text-xs text-slate-500">{field.options.length} options</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-xs">
                      {field.is_required_default ? (
                        <><CheckCircle className="w-3 h-3 text-emerald-400" /><span className="text-emerald-400">Required</span></>
                      ) : (
                        <><AlertCircle className="w-3 h-3 text-amber-400" /><span className="text-amber-400">Optional</span></>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => handleEdit(field)}
                      className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(field.id)}
                      className="p-1.5 rounded hover:bg-red-900/50 text-red-400 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 border-2 border-dashed border-slate-700/50 rounded-xl">
            <FormInput className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No information fields created yet</p>
            <p className="text-slate-500 text-sm mt-1">Click &quot;Add Information Field&quot; to create one</p>
          </div>
        )
      )}
    </div>
  );
};

export default InformationFieldsTab;
